import { ConfirmationDialog } from '@/components/core/ConfirmationDialog'
import { EditableField } from '@/components/core/EditableField'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { SkeletonTableRows } from '@/pages/ProjectsPage/components/SkeletonTableRows'
import { Tool } from '@/services/queries/tools'
import { TrashIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'

const headerColumns = ['Tool', 'Action']

export const OrgToolsTable = ({
  tools = [],
  isLoading,
  onDelete,
  onUpdate,
}: {
  tools: Tool[] | undefined
  isLoading: boolean
  onDelete: (params: { toolId: string }) => void
  onUpdate: (params: { toolId: string; name: string }) => void
}) => {
  if (isLoading) {
    return <SkeletonTableRows />
  }

  return (
    <div className="p-4">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="text-left text-xs font-medium text-gray-400 tracking-wider">
          <tr>
            {headerColumns.map((header, index) => (
              <th key={index} scope="col" className="px-6 py-3">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tools.map((tool) => (
            <tr
              className="whitespace-nowrap text-sm font-medium text-gray-900"
              key={tool.id}
            >
              <td className="px-6 py-4">
                <ToolNameField tool={tool} onRename={onUpdate} />
              </td>
              <td className="px-6">
                <DeleteButton tool={tool} onDelete={onDelete} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ToolNameField({
  tool,
  onRename,
}: {
  tool: Tool
  onRename: (params: { toolId: string; name: string }) => void
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [name, setName] = useState(tool.name)

  useEffect(() => {
    setName(tool.name)
  }, [tool.name])

  const handleRenameTool = () => {
    onRename({ toolId: tool.id, name })
    setShowConfirmationModal(false)
  }

  return (
    <>
      <ConfirmationDialog
        title={`Rename ${tool.name} to ${name}?`}
        isOpen={showConfirmationModal}
        onConfirm={handleRenameTool}
        closeDialog={() => {
          setName(tool.name)
          setShowConfirmationModal(false)
        }}
      >
        <span>Any documents that reference this tool will be updated.</span>
      </ConfirmationDialog>
      <ContextMenu>
        <ContextMenuTrigger>
          <EditableField
            value={name}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onSaveHandler={(newName) => {
              if (newName === tool.name) return
              setName(newName)
              setShowConfirmationModal(true)
            }}
          />
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onMouseDown={() => setIsEditing(true)}>
            Rename
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </>
  )
}

function DeleteButton({
  tool,
  onDelete,
}: {
  tool: Tool
  onDelete: (params: { toolId: string }) => void
}) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const handleDeleteTool = () => {
    onDelete({ toolId: tool.id })
    setShowConfirmationModal(false)
  }

  return (
    <>
      <ConfirmationDialog
        title={`Delete ${tool.name}?`}
        isOpen={showConfirmationModal}
        onConfirm={handleDeleteTool}
        closeDialog={() => setShowConfirmationModal(false)}
      >
        <span>Deleting this tool will remove it from all documents.</span>
      </ConfirmationDialog>
      <button onClick={() => setShowConfirmationModal(true)}>
        <TrashIcon className="h-4 text-gray-500" />
      </button>
    </>
  )
}
